var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { convertStringToDate, TYPE_POPUP } from '@libs/@core/components/date-picker/utils/dateUtils';
import { BaseCalendarContent } from '@libs/@core/components/date-picker/components';
export var BaseCalendarContext = createContext(null);
export var BaseCalendarHeaderContext = createContext(null);
/**
 *
 * This is a base calendar component that is used to render the calendar view
 *
 * @param value - The selected date value
 *
 * @param activeInput - The active input field
 *
 * @param multiple - The flag to determine if the calendar is a range date picker
 *
 * @param maxDate - Max date value
 *
 * @param minDate - Min date value
 *
 * @param format - The format of the date must be ''DD/MM/YYYY'' or ''YYYY-MM-DD'' or ''MM/DD/YYYY''
 *
 * @param onChangeDate - The function to handle the date change
 *
 * @param handleChangeDateValue - The function to handle the date change value
 *
 * @param handleInputFocus - The function to handle the input focus
 *
 * @returns The base calendar component
 *
 */
var BaseCalendar = function (_a) {
    var value = _a.value, activeInput = _a.activeInput, multiple = _a.multiple, maxDate = _a.maxDate, minDate = _a.minDate, _b = _a.format, format = _b === void 0 ? 'DD/MM/YYYY' : _b, onChangeDate = _a.onChangeDate, handleChangeDateValue = _a.handleChangeDateValue, handleInputFocus = _a.handleInputFocus;
    var popupRef = useRef(null);
    var _c = useState(TYPE_POPUP.DATE), type = _c[0], setType = _c[1];
    var _d = useState(multiple ? [new Date(), new Date(new Date().setMonth(new Date().getMonth() + 1))] : [new Date()]), selectedDateRange = _d[0], setSelectedDateRange = _d[1];
    var maxDateValue = useMemo(function () { return (maxDate ? convertStringToDate(maxDate, format) : null); }, [maxDate]);
    var minDateValue = useMemo(function () { return (minDate ? convertStringToDate(minDate, format) : null); }, [minDate]);
    var rangeYears = useMemo(function () {
        var startYear = Math.floor(selectedDateRange[0].getFullYear() / 10) * 10;
        var endYear = startYear + 9;
        return [startYear, endYear];
    }, [selectedDateRange]);
    useEffect(function () {
        var popup = popupRef.current;
        if (!popup)
            return;
        var popupRect = popup.getBoundingClientRect();
        var windowWidth = window.innerWidth;
        if (popupRect.right > windowWidth) {
            var overflow = popupRect.right - windowWidth;
            popup.style.left = "-".concat(overflow, "px");
        }
        if (popupRect.left < 0) {
            popup.style.left = '0px';
        }
    }, [selectedDateRange, type, multiple]);
    var handlePrevMonth = function () {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [
                new Date(prev[0].getFullYear(), prev[0].getMonth() - 1, 1),
                new Date(prev[1].getFullYear(), prev[1].getMonth() - 1, 1, 23, 59, 59, 999)
            ]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear(), prev[0].getMonth() - 1, 1)]; });
        }
    };
    var handleNextMonth = function () {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [
                new Date(prev[0].getFullYear(), prev[0].getMonth() + 1, 1),
                new Date(prev[1].getFullYear(), prev[1].getMonth() + 1, 1, 23, 59, 59, 999)
            ]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear(), prev[0].getMonth() + 1, 1)]; });
        }
    };
    var handlePrevYear = function () {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [
                new Date(prev[0].getFullYear() - 1, prev[0].getMonth(), 1),
                new Date(prev[1].getFullYear() - 1, prev[1].getMonth(), 1, 23, 59, 59, 999)
            ]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear() - 1, prev[0].getMonth(), 1)]; });
        }
    };
    var handleNextYear = function () {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [
                new Date(prev[0].getFullYear() + 1, prev[0].getMonth(), 1),
                new Date(prev[1].getFullYear() + 1, prev[1].getMonth(), 1, 23, 59, 59, 999)
            ]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear() + 1, prev[0].getMonth(), 1)]; });
        }
    };
    var handlePrevDecade = function () {
        setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear() - 10, prev[0].getMonth(), 1)]; });
    };
    var handleNextDecade = function () {
        setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear() + 10, prev[0].getMonth(), 1)]; });
    };
    var handleChangeDateByEnd = function (date) {
        var rangeDate = __spreadArray([], value, true);
        if (!value[0]) {
            rangeDate = [date];
            handleChangeDateValue(rangeDate);
            return rangeDate;
        }
        if (date < value[0]) {
            rangeDate = [date, new Date(value[0].getFullYear(), value[0].getMonth(), value[0].getDate(), 23, 59, 59, 999)];
            handleInputFocus('start');
        }
        else {
            rangeDate = [value[0], new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)];
        }
        handleChangeDateValue(rangeDate);
        return rangeDate;
    };
    var handleChangeDateByStart = function (date) {
        var rangeDate = __spreadArray([], value, true);
        if (!value[1]) {
            rangeDate = [date];
            handleChangeDateValue(rangeDate);
            setTimeout(function () {
                handleInputFocus('end');
            }, 0);
            return rangeDate;
        }
        if (date > value[1]) {
            rangeDate = [value[1], new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)];
            setTimeout(function () {
                handleInputFocus('end');
            }, 0);
        }
        else {
            rangeDate = [date, new Date(value[1].getFullYear(), value[1].getMonth(), value[1].getDate(), 23, 59, 59, 999)];
        }
        handleChangeDateValue(rangeDate);
        return rangeDate;
    };
    var handleDateClick = function (date) {
        if (minDate && date < minDateValue)
            return;
        if (maxDate && date > maxDateValue)
            return;
        if (multiple) {
            var rangeDate = __spreadArray([], value, true);
            if (activeInput === 'start')
                rangeDate = handleChangeDateByStart(date);
            if (activeInput === 'end' || (Array.isArray(value) && value.length === 1))
                rangeDate = handleChangeDateByEnd(date);
            onChangeDate(rangeDate);
        }
        else {
            handleChangeDateValue([date]);
            onChangeDate(date);
        }
    };
    var handleChangeType = useCallback(function (newType) {
        setType(newType);
    }, [type]);
    var handleSelectMonth = function (month) {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [
                new Date(prev[0].getFullYear(), month, 1),
                new Date(prev[1].getFullYear(), month + 1, 1)
            ]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(prev[0].getFullYear(), month, 1)]; });
        }
        setType(TYPE_POPUP.DATE);
    };
    var handleSelectYear = function (year) {
        if (multiple) {
            setSelectedDateRange(function (prev) { return [new Date(year, prev[0].getMonth(), 1), new Date(year, prev[1].getMonth(), 1)]; });
        }
        else {
            setSelectedDateRange(function (prev) { return [new Date(year, prev[0].getMonth(), 1)]; });
        }
        setType(TYPE_POPUP.MONTH);
    };
    var renderCalendar = useMemo(function () {
        if (multiple) {
            if (type === TYPE_POPUP.MONTH || type === TYPE_POPUP.YEAR) {
                return (_jsx(BaseCalendarContent, { selectedDate: value, currentDate: selectedDateRange[0], handleDateClick: handleDateClick }));
            }
            return Array.from({ length: 2 }).map(function (_, index) { return (_jsx(BaseCalendarContent, { selectedDate: value, position: index === 0 ? 'start' : 'end', currentDate: selectedDateRange[index], handleDateClick: handleDateClick }, index)); });
        }
        return (_jsx(BaseCalendarContent, { selectedDate: value, currentDate: selectedDateRange[0], handleDateClick: handleDateClick }));
    }, [value, selectedDateRange, type, activeInput, handleDateClick, multiple]);
    var contentData = {
        type: type,
        multiple: multiple,
        rangeYears: rangeYears,
        maxDate: maxDateValue,
        minDate: minDateValue,
        handleChangeType: handleChangeType,
        handleSelectMonth: handleSelectMonth,
        handleSelectYear: handleSelectYear
    };
    var headerContent = {
        handlePrevMonth: handlePrevMonth,
        handleNextMonth: handleNextMonth,
        handlePrevYear: handlePrevYear,
        handleNextYear: handleNextYear,
        handleNextDecade: handleNextDecade,
        handlePrevDecade: handlePrevDecade
    };
    return (_jsx(BaseCalendarContext.Provider, { value: contentData, children: _jsx(BaseCalendarHeaderContext.Provider, { value: headerContent, children: _jsx("div", { ref: popupRef, className: clsx([
                    'absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 border border-gray-200',
                    {
                        'w-[500px]': type === TYPE_POPUP.DATE && multiple,
                        'w-[280px]': type === TYPE_POPUP.MONTH || type === TYPE_POPUP.YEAR,
                        'w-[300px]': type === TYPE_POPUP.DATE && !multiple
                    }
                ]), children: _jsx("div", { className: 'flex space-x-4', children: renderCalendar }) }) }) }));
};
export default memo(BaseCalendar);
